

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function StorageUnitsSection({ caseId, sofaResponse }) {
  const [units, setUnits] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const emptyPerson = { name: '', street: '', city: '', state: '', zip: '' };
  const [newUnit, setNewUnit] = useState({
    facility_name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    others_with_access: [],
    contents_description: '',
    still_has: true
  });
  const [editUnit, setEditUnit] = useState(null);

  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-storage-units/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch storage units');
        setUnits(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  function openAddModal() {
    setNewUnit({
      facility_name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      others_with_access: [],
      contents_description: '',
      still_has: true
    });
    setModalOpen(true);
  }

  function openEditModal(item) {
    setEditUnit({ ...item });
    setEditModalOpen(true);
  }

  function addPerson(isEdit = false) {
    if (isEdit) {
      setEditUnit(prev => ({
        ...prev,
        others_with_access: [...(prev.others_with_access || []), emptyPerson]
      }));
    } else {
      setNewUnit(prev => ({
        ...prev,
        others_with_access: [...prev.others_with_access, emptyPerson]
      }));
    }
  }

  function removePerson(index, isEdit = false) {
    if (isEdit) {
      setEditUnit(prev => ({
        ...prev,
        others_with_access: prev.others_with_access.filter((_, i) => i !== index)
      }));
    } else {
      setNewUnit(prev => ({
        ...prev,
        others_with_access: prev.others_with_access.filter((_, i) => i !== index)
      }));
    }
  }

  function updatePersonField(index, field, value, isEdit = false) {
    const box = isEdit ? editUnit : newUnit;
    const list = [...(box.others_with_access || [])];
    list[index] = { ...list[index], [field]: value };
    if (isEdit) {
      setEditUnit(prev => ({ ...prev, others_with_access: list }));
    } else {
      setNewUnit(prev => ({ ...prev, others_with_access: list }));
    }
  }

  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-storage-units/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newUnit)
        }
      );
      if (!res.ok) throw new Error();
      const created = await res.json();
      setUnits(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error('Failed to add unit', err);
    }
  }

  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-storage-units/${editUnit.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editUnit)
        }
      );
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setUnits(prev => prev.map(u => (u.id === updated.id ? updated : u)));
      setEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update unit', err);
    }
  }

  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-storage-units/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error();
      setUnits(prev => prev.filter(u => u.id !== id));
    } catch (err) {
      console.error('Failed to delete unit', err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 22: Storage Units
      </h2>
        <p className="text-sm text-gray-600 mb-4">
            List storage units away from your home used within the last year.
        </p>
      {units.length === 0 ? (
        <p>No storage units recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {units.map(unit => (
            <div
              key={unit.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(unit)}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(unit.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                00-2 0z"/></svg>
              </button>
              <h3 className="font-medium text-lg">{unit.facility_name}</h3>
              <p className="mt-1 text-sm">
                {unit.street1}{unit.street2 ? `, ${unit.street2}` : ''}, {unit.city}, {unit.state} {unit.zip_code}
              </p>
              {unit.others_with_access?.length > 0 && (
                <p className="mt-2 text-sm"><strong>Others:</strong> {unit.others_with_access.map(p => p.name).join(', ')}</p>
              )}
              {unit.contents_description && (
                <p className="mt-2 text-sm"><strong>Contents:</strong> {unit.contents_description}</p>
              )}
              <p className="mt-2 text-sm"><strong>Still Has:</strong> {unit.still_has ? 'Yes' : 'No'}</p>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Storage Unit
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Add Storage Unit</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Facility Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newUnit.facility_name}
                onChange={e => setNewUnit(prev => ({ ...prev, facility_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newUnit.street1}
                  onChange={e => setNewUnit(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newUnit.street2}
                  onChange={e => setNewUnit(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newUnit.city}
                  onChange={e => setNewUnit(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newUnit.state}
                  onChange={e => setNewUnit(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newUnit.zip_code}
                  onChange={e => setNewUnit(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            {/* Others with access same as SafetyDepositSection */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Others With Access</label>
              {newUnit.others_with_access.map((p, idx) => (
                <div key={idx} className="border p-2 rounded mb-2">
                  <div className="flex justify-end">
                    <button onClick={() => removePerson(idx)}>
                      <span className="text-red-600">Remove</span>
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Name"
                    className="w-full border rounded px-2 py-1 mb-1"
                    value={p.name}
                    onChange={e => updatePersonField(idx, 'name', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Street"
                    className="w-full border rounded px-2 py-1 mb-1"
                    value={p.street}
                    onChange={e => updatePersonField(idx, 'street', e.target.value)}
                  />
                  <div className="grid grid-cols-7 gap-4">
                    <div className="col-span-4">
                      <input
                        type="text"
                        placeholder="City"
                        className="w-full border rounded px-2 py-1 mb-1"
                        value={p.city}
                        onChange={e => updatePersonField(idx, 'city', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1">
                      <input
                        type="text"
                        placeholder="State"
                        className="w-full border rounded px-2 py-1 mb-1"
                        value={p.state}
                        onChange={e => updatePersonField(idx, 'state', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="text"
                        placeholder="Zip"
                        className="w-full border rounded px-2 py-1 mb-1"
                        value={p.zip}
                        onChange={e => updatePersonField(idx, 'zip', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={() => addPerson()} className="px-3 py-1 bg-green-600 text-white rounded">
                Add Person
              </button>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Contents Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={newUnit.contents_description}
                onChange={e => setNewUnit(prev => ({ ...prev, contents_description: e.target.value }))}
              />
            </div>
            <div className="mb-3 flex items-center space-x-2">
              <input
                type="checkbox"
                checked={newUnit.still_has}
                onChange={e => setNewUnit(prev => ({ ...prev, still_has: e.target.checked }))}
              />
              <label className="text-sm">I still have access</label>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Edit Storage Unit</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Facility Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editUnit.facility_name}
                onChange={e => setEditUnit(prev => ({ ...prev, facility_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editUnit.street1}
                  onChange={e => setEditUnit(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editUnit.street2}
                  onChange={e => setEditUnit(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editUnit.city}
                  onChange={e => setEditUnit(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editUnit.state}
                  onChange={e => setEditUnit(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editUnit.zip_code}
                  onChange={e => setEditUnit(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Others With Access</label>
              {editUnit.others_with_access?.map((p, idx) => (
                <div key={idx} className="border p-2 rounded mb-2">
                  <div className="flex justify-end">
                    <button onClick={() => removePerson(idx, true)}>
                      <span className="text-red-600">Remove</span>
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Name"
                    className="w-full border rounded px-2 py-1 mb-1"
                    value={p.name}
                    onChange={e => updatePersonField(idx, 'name', e.target.value, true)}
                  />
                  <input
                    type="text"
                    placeholder="Street"
                    className="w-full border rounded px-2 py-1 mb-1"
                    value={p.street}
                    onChange={e => updatePersonField(idx, 'street', e.target.value, true)}
                  />
                  <div className="grid grid-cols-7 gap-4">
                    <div className="col-span-4">
                      <input
                        type="text"
                        placeholder="City"
                        className="w-full border rounded px-2 py-1 mb-1"
                        value={p.city}
                        onChange={e => updatePersonField(idx, 'city', e.target.value, true)}
                      />
                    </div>
                    <div className="col-span-1">
                      <input
                        type="text"
                        placeholder="State"
                        className="w-full border rounded px-2 py-1 mb-1"
                        value={p.state}
                        onChange={e => updatePersonField(idx, 'state', e.target.value, true)}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="text"
                        placeholder="Zip"
                        className="w-full border rounded px-2 py-1 mb-1"
                        value={p.zip}
                        onChange={e => updatePersonField(idx, 'zip', e.target.value, true)}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={() => addPerson(true)} className="px-3 py-1 bg-green-600 text-white rounded">
                Add Person
              </button>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Contents Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={editUnit.contents_description}
                onChange={e => setEditUnit(prev => ({ ...prev, contents_description: e.target.value }))}
              />
            </div>
            <div className="mb-3 flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editUnit.still_has}
                onChange={e => setEditUnit(prev => ({ ...prev, still_has: e.target.checked }))}
              />
              <label className="text-sm">I still have access</label>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
);
}